/**
 * Dashboard Page (Wizard Home)
 * Displays the main wizard dashboard with step cards.
 * Primary admin can edit organization (name, industry, address) via modal; same form as create.
 * SOLID: page orchestrates only; logic in hooks and constants.
 */

import React, { useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import MainPageLayout from '../../components/common/MainPageLayout';
import DashboardOrganizationCreated from './DashboardOrganizationCreated';
import { WizardDashboardSkeleton } from './components/WizardDashboardSkeleton';
import { fetchOrganizationByIdThunk } from '../../store/slices/organizationThunks';
import ROUTES from '../../constants/routes';
import {
  getOrganizationIdFromState,
  getOrganizationName,
  ORGANIZATION_ID_STORAGE_KEY,
} from './constants';
import { useDashboardData, useStepNavigation, useDashboardDerivedState } from './hooks';
import { canInviteUsers, isPrimaryAdminUser } from '../../utils/accessScopeUtils';
import { getStoredUser } from '../../utils/authStorageUtils';

export function DashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  /** Cleaning standards (step 3) is always part of the flow; no feature flag. */
  const cleaningStandardsEnabled = true;

  const organizationIdFromState = useSelector(getOrganizationIdFromState);
  const organizationId =
    organizationIdFromState ||
    (typeof localStorage !== 'undefined'
      ? localStorage.getItem(ORGANIZATION_ID_STORAGE_KEY)
      : null) ||
    null;
  const authUser = useSelector((state) => state.auth.user);
  const organization = useSelector((state) => state.organization.data);
  const storedUser = getStoredUser();
  const effectiveAuthUser = authUser?.data ?? authUser;
  const isPrimaryAdmin = isPrimaryAdminUser(effectiveAuthUser) || isPrimaryAdminUser(storedUser);
  const userCanInviteUsers = canInviteUsers(effectiveAuthUser) || canInviteUsers(storedUser);

  const organizationName = organization?.name ?? getOrganizationName();
  const onboardingDataFromRedux = useSelector((state) => state.auth.onboardingData);

  useEffect(() => {
    if (organizationId) {
      dispatch(fetchOrganizationByIdThunk(organizationId));
    }
  }, [organizationId, dispatch]);

  const { wizardStatus, onboardingStatus, loading } = useDashboardData(
    organizationId,
    location.pathname,
    onboardingDataFromRedux,
  );

  const { handleStepClick } = useStepNavigation(wizardStatus, cleaningStandardsEnabled, navigate);

  const { stepConfigs, filteredWizardStatus, step1Completed, step3Completed, canGoToDashboard } =
    useDashboardDerivedState(
      wizardStatus,
      onboardingStatus,
      cleaningStandardsEnabled,
      userCanInviteUsers,
    );

  const handleGoToDashboard = useCallback(() => {
    navigate(ROUTES.DASHBOARD);
  }, [navigate]);

  if (loading) {
    return (
      <MainPageLayout customHeaderProps={{ hideBackButton: true }}>
        <WizardDashboardSkeleton organizationName={organizationName} />
      </MainPageLayout>
    );
  }

  return (
    <MainPageLayout customHeaderProps={{ hideBackButton: true }}>
      <DashboardOrganizationCreated
        organizationName={organizationName}
        isPrimaryAdmin={isPrimaryAdmin}
        canEditOrganization={!!organization}
        wizardStatus={filteredWizardStatus}
        onStepClick={handleStepClick}
        stepConfigs={stepConfigs}
        step1Completed={step1Completed}
        step3Completed={step3Completed}
        canGoToDashboard={canGoToDashboard}
        onGoToDashboard={handleGoToDashboard}
      />
    </MainPageLayout>
  );
}
